import { HttpClient, HttpContext } from '@angular/common/http';
import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { Bundle, BundleEntry, Specimen, Medication, MedicationDispense, MedicationAdministration, ServiceRequest, Location, MedicationRequest, Patient, Encounter, Invoice, ChargeItem, DiagnosticReport, SupplyDelivery, ChargeItemDefinition, HealthcareService } from 'fhir/r4';
import { EMPTY, forkJoin, map, catchError, of, tap, expand, Observable, from, mergeMap, switchMap, delay, concatMap } from 'rxjs';
import { StateService } from '../shared/state.service';
import { backendEndPointToken, fhirMaxAllAtOnceCountToken } from '../app.config';
import { DisableCountParam, DisableSortParam, LoadingUIEnabled } from '../loading.interceptor';
import { normalizeFhirNextLinkUrl } from '../shared/utility.service';

const bundleToResources = <T>(bundle: Bundle<T> | null | undefined): T[] =>
  bundle?.entry?.map((entry: BundleEntry<T>) => entry.resource as T).filter(Boolean) ?? [];

const getNextLinkUrl = (bundle: Bundle<any> | null | undefined, baseUrl: string): string | null => {
  const rawUrl = (bundle?.link || []).find((l: any) => l?.relation === 'next')?.url || null;
  return normalizeFhirNextLinkUrl(rawUrl, baseUrl);
};

type PageResult<T> = {
  resources: T[];
  nextUrl: string | null;
  total: number | null;
};

const wrapResource = <T extends { id?: string }>(resourceType: string, res: T) => ({
  referenceId: res?.id ? `${resourceType}/${res.id}` : null,
  savedStatus: 'saved' as const,
  actualResource: res,
});

const setSearchParam = (url: string, key: string, value: string): string => {
  try {
    const u = new URL(url);
    u.searchParams.set(key, value);
    return u.toString();
  } catch {
    const parts = url.split('#');
    const base = parts[0];
    const hash = parts.length > 1 ? `#${parts.slice(1).join('#')}` : '';
    const [path, query = ''] = base.split('?');
    const params = new URLSearchParams(query);
    params.set(key, value);
    const qs = params.toString();
    return `${path}${qs ? `?${qs}` : ''}${hash}`;
  }
};

const mergeWrappedDeduped = <T extends { id?: string }>(
  existingWrapped: Array<{ referenceId: string | null; actualResource: T }> | null | undefined,
  incomingResources: T[],
  resourceType: string
) => {
  const mapByRef = new Map<string, any>();
  const withoutRef: any[] = [];

  for (const w of (existingWrapped ?? [])) {
    const ref = w?.referenceId;
    if (ref) mapByRef.set(ref, w);
    else withoutRef.push(w);
  }

  for (const r of (incomingResources ?? [])) {
    const wrapped = wrapResource(resourceType, r);
    if (wrapped.referenceId) {
      if (!mapByRef.has(wrapped.referenceId)) mapByRef.set(wrapped.referenceId, wrapped);
    } else {
      // no id -> can't dedupe reliably
      withoutRef.push(wrapped);
    }
  }

  return [...mapByRef.values(), ...withoutRef];
};

export const appWrapperDataResolver: ResolveFn<boolean> = (route) => {
  const baseUrl = inject(backendEndPointToken);
  const http = inject(HttpClient);
  const stateService = inject(StateService);
  const maxAllAtOnceCount = inject(fhirMaxAllAtOnceCountToken);

  // Background loading context
  const silentCtx = new HttpContext()
    .set(LoadingUIEnabled, false)
    .set(DisableCountParam, true)
    .set(DisableSortParam, true);

  // Start background fetch in prioritized batches
  loadOrgResourcesInBackground(
    http,
    baseUrl || 'https://elikita-server.daalitech.com',
    stateService,
    maxAllAtOnceCount || 500,
    silentCtx
  );

  // Return immediately to allow fast route activation
  return of(true);
};

/**
 * Loads organization-wide resources asynchronously in prioritized batches.
 */
function loadOrgResourcesInBackground(
  http: HttpClient,
  baseUrl: string,
  stateService: StateService,
  maxAllAtOnceCount: number,
  silentCtx: HttpContext
): void {
  const maxBackgroundPagesDefault = 2;
  const maxBackgroundPagesByResource: Record<string, number> = {
    Patient: 10,
    Encounter: 10,
    ServiceRequest: 10,
    ChargeItem: 10,
    Invoice: 10,
    ChargeItemDefinition: 10,
    CodeSystem: 1,
    ValueSet: 1,
  };

  const maxConcurrentBackgroundResourcePagers = 2;
  const backgroundStreams: Observable<unknown>[] = [];

  const fetchFirstPage = <T>(url: string): Observable<PageResult<T>> =>
    http.get<Bundle<T>>(url, { context: silentCtx }).pipe(
      map((bundle) => ({
        resources: bundleToResources(bundle),
        nextUrl: getNextLinkUrl(bundle, baseUrl),
        total: typeof bundle?.total === 'number' ? bundle.total : null,
      })),
      catchError(() => of({ resources: [], nextUrl: null, total: null }))
    );

  const loadRemainingPagesIntoSubject = <T extends { id?: string }>(
    nextUrl: string | null,
    resourceType: string,
    subject: any
  ): Observable<unknown> => {
    if (!nextUrl) return EMPTY;
    let pagesFetched = 0;
    const maxPages = maxBackgroundPagesByResource[resourceType] ?? maxBackgroundPagesDefault;
    if (maxPages <= 0) return EMPTY;

    return http.get<Bundle<T>>(nextUrl, { context: silentCtx }).pipe(
      expand((bundle) => {
        pagesFetched += 1;
        if (pagesFetched >= maxPages) return EMPTY;
        const n = getNextLinkUrl(bundle, baseUrl);
        return n ? http.get<Bundle<T>>(n, { context: silentCtx }) : EMPTY;
      }),
      map((bundle) => bundleToResources<T>(bundle)),
      tap((resources) => {
        if (!resources?.length) return;
        const current = subject.getValue?.() ?? [];
        subject.next(mergeWrappedDeduped(current, resources, resourceType));
      }),
      catchError(() => EMPTY)
    );
  };

  const loadAllAtOnceIfNoNext = <T extends { id?: string }>(
    firstPage: PageResult<T>,
    resourceType: string,
    subject: any,
    baseSearchUrl: string
  ): Observable<unknown> => {
    const got = firstPage?.resources?.length ?? 0;
    let total = firstPage?.total ?? maxAllAtOnceCount;
    if (total <= got) return EMPTY;

    const capped = Math.min(total, maxAllAtOnceCount);
    if (capped <= got) return EMPTY;

    const url = setSearchParam(setSearchParam(baseSearchUrl, '_count', String(capped)), '_total', 'accurate');
    return http.get<Bundle<T>>(url, { context: silentCtx }).pipe(
      map((bundle) => bundleToResources<T>(bundle)),
      tap((resources) => {
        const merged = mergeWrappedDeduped([], resources, resourceType);
        subject.next(merged);
      }),
      catchError(() => EMPTY)
    );
  };

  const processResult = <T extends { id?: string }>(
    result: PageResult<T>,
    resourceType: string,
    subject: any,
    baseSearchUrl: string
  ) => {
    subject.next((result.resources || []).map(r => wrapResource(resourceType, r)));
    backgroundStreams.push(loadRemainingPagesIntoSubject(result.nextUrl, resourceType, subject));
    backgroundStreams.push(loadAllAtOnceIfNoNext(result, resourceType, subject, baseSearchUrl));
  };

  // BATCH 1: Core Shell Data (Immediate)
  const batch1$ = forkJoin({
    patient: fetchFirstPage<Patient>(`${baseUrl}/Patient?_count=199&_total=accurate`),
    locations: fetchFirstPage<Location>(`${baseUrl}/Location?_count=199&_total=accurate`),
    healthcareServices: fetchFirstPage<HealthcareService>(`${baseUrl}/HealthcareService?_count=199&_total=accurate`),
    codeSystems: fetchFirstPage<any>(`${baseUrl}/CodeSystem?_count=500&_total=accurate`),
  });

  // BATCH 2: Clinical Data (300ms delay)
  const batch2$ = of(null).pipe(
    delay(300),
    switchMap(() => forkJoin({
      encounters: fetchFirstPage<Encounter>(`${baseUrl}/Encounter?_count=199&_total=accurate`),
      serviceRequests: fetchFirstPage<ServiceRequest>(`${baseUrl}/ServiceRequest?_count=199&_total=accurate`),
      specimens: fetchFirstPage<Specimen>(`${baseUrl}/Specimen?_count=199&_total=accurate`),
      medications: fetchFirstPage<Medication>(`${baseUrl}/Medication?_count=199&_total=accurate`),
      medicationRequests: fetchFirstPage<MedicationRequest>(`${baseUrl}/MedicationRequest?_count=199&_total=accurate`),
    }))
  );

  // BATCH 3: Secondary/Billing Data (600ms delay)
  const batch3$ = of(null).pipe(
    delay(600),
    switchMap(() => forkJoin({
      medicationDispenses: fetchFirstPage<MedicationDispense>(`${baseUrl}/MedicationDispense?_count=199&_total=accurate`),
      medicationAdministrations: fetchFirstPage<MedicationAdministration>(`${baseUrl}/MedicationAdministration?_count=199&_total=accurate`),
      chargeItemDefinitions: fetchFirstPage<ChargeItemDefinition>(`${baseUrl}/ChargeItemDefinition?_count=199&_total=accurate`),
      chargeitem: fetchFirstPage<ChargeItem>(`${baseUrl}/ChargeItem?_count=199&_total=accurate`),
      invoices: fetchFirstPage<Invoice>(`${baseUrl}/Invoice?_count=199&_total=accurate`),
      diagnosticReports: fetchFirstPage<DiagnosticReport>(`${baseUrl}/DiagnosticReport?_count=199&_total=accurate`),
      supplyDeliveries: fetchFirstPage<SupplyDelivery>(`${baseUrl}/SupplyDelivery?_count=500&_sort=-_lastUpdated&_total=accurate`),
    }))
  );

  batch1$.pipe(
    tap(res => {
      processResult(res.patient, 'Patient', stateService.orgWideResources.patient, `${baseUrl}/Patient`);
      processResult(res.locations, 'Location', stateService.orgWideResources.locations, `${baseUrl}/Location`);
      processResult(res.healthcareServices, 'HealthcareService', stateService.orgWideResources.healthcareServices, `${baseUrl}/HealthcareService`);
      processResult(res.codeSystems, 'CodeSystem', stateService.orgWideResources.codeSystems, `${baseUrl}/CodeSystem`);
    }),
    concatMap(() => batch2$),
    tap(res => {
      processResult(res.encounters, 'Encounter', stateService.orgWideResources.encounters, `${baseUrl}/Encounter`);
      processResult(res.serviceRequests, 'ServiceRequest', stateService.orgWideResources.serviceRequests, `${baseUrl}/ServiceRequest`);
      processResult(res.specimens, 'Specimen', stateService.orgWideResources.specimens, `${baseUrl}/Specimen`);
      processResult(res.medications, 'Medication', stateService.orgWideResources.medications, `${baseUrl}/Medication`);
      processResult(res.medicationRequests, 'MedicationRequest', stateService.orgWideResources.medicationRequests, `${baseUrl}/MedicationRequest`);
    }),
    concatMap(() => batch3$),
    tap(res => {
      processResult(res.medicationDispenses, 'MedicationDispense', stateService.orgWideResources.medicationDispenses, `${baseUrl}/MedicationDispense`);
      processResult(res.medicationAdministrations, 'MedicationAdministration', stateService.orgWideResources.medicationAdministrations, `${baseUrl}/MedicationAdministration`);
      processResult(res.chargeItemDefinitions, 'ChargeItemDefinition', stateService.orgWideResources.chargeItemDefinitions, `${baseUrl}/ChargeItemDefinition`);
      processResult(res.chargeitem, 'ChargeItem', stateService.orgWideResources.chargeItems, `${baseUrl}/ChargeItem`);
      processResult(res.invoices, 'Invoice', stateService.orgWideResources.invoices, `${baseUrl}/Invoice`);
      processResult(res.diagnosticReports, 'DiagnosticReport', stateService.orgWideResources.diagnosticReports, `${baseUrl}/DiagnosticReport`);
      processResult(res.supplyDeliveries, 'SupplyDelivery', stateService.orgWideResources.supplyDeliveries, `${baseUrl}/SupplyDelivery?_sort=-_lastUpdated`);

      // Execute background pagers with concurrency limit
      from(backgroundStreams).pipe(
        mergeMap(s => s, maxConcurrentBackgroundResourcePagers),
        catchError(() => EMPTY)
      ).subscribe();
    }),
    catchError(err => {
      console.error('Failed organization resource background load:', err);
      return of(null);
    })
  ).subscribe();
}

